/* Oakline — product page: variants, gallery, personalization, add to cart */
(() => {
  const theme = window.theme || {};
  const strings = theme.strings || {};

  /* ---------- Product info: variant selection ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      this.data = json ? JSON.parse(json.textContent) : { variants: [], options: [] };
      this.picker = this.querySelector('variant-picker');
      this.gallery = this.querySelector('media-gallery');
      this.sectionId = this.dataset.section;
      const idInput = this.querySelector('[data-variant-id]');
      this.currentVariant = this.data.variants.find((v) => String(v.id) === String(idInput && idInput.value)) || this.data.variants[0];
      if (this.picker) this.picker.addEventListener('change', (event) => this.onOptionChange(event));
      this.initReadMore();
      this.initShare();
    }

    get selectedOptions() {
      return [...this.picker.querySelectorAll('[data-option-index]')].map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.data.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
    }

    onOptionChange(event) {
      const group = event.target.closest('[data-option-index]');
      const changedIndex = group ? Number(group.dataset.optionIndex) : -1;
      const options = this.selectedOptions;
      let variant = this.findVariant(options);

      if ((!variant || !variant.available) && changedIndex > -1) {
        // Keep the value the customer just picked and find the closest available combination
        const score = (v) =>
          (v.available ? 1000 : 0) + v.options.reduce((sum, value, i) => sum + (value === options[i] ? 10 - i : 0), 0);
        const best = this.data.variants
          .filter((v) => v.options[changedIndex] === options[changedIndex])
          .sort((a, b) => score(b) - score(a))[0];
        if (best && (!variant || best.available)) {
          variant = best;
          this.setOptions(best.options);
        }
      }
      this.currentVariant = variant || null;
      this.render();
    }

    setOptions(values) {
      values.forEach((value, i) => {
        const group = this.picker.querySelector(`[data-option-index="${i}"]`);
        if (!group) return;
        const select = group.querySelector('select');
        if (select) {
          select.value = value;
          return;
        }
        group.querySelectorAll('input').forEach((input) => {
          input.checked = input.value === value;
        });
      });
    }

    render() {
      const variant = this.currentVariant;
      this.updateOptionStates();
      this.querySelectorAll('[data-selected-value]').forEach((label) => {
        const group = label.closest('[data-option-index]');
        const checked = group && group.querySelector('input:checked');
        label.textContent = checked ? checked.value : '';
      });

      const form = document.getElementById(this.dataset.formId);
      document.querySelectorAll(`#${this.dataset.formId} [data-variant-id], #product-form-installments-${this.sectionId} input[name="id"]`).forEach((input) => {
        input.value = variant ? variant.id : '';
      });

      this.renderPrice(variant);
      this.renderButton(variant, form);

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.featured_media && this.gallery && this.gallery.showMedia) this.gallery.showMedia(variant.featured_media.id);
      }

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, sectionId: this.sectionId } }));
    }

    updateOptionStates() {
      if (!this.picker) return;
      const selected = this.selectedOptions;
      this.picker.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const isAvailable = (value) =>
          this.data.variants.some(
            (v) => v.available && v.options[index] === value && v.options.slice(0, index).every((o, j) => o === selected[j])
          );
        group.querySelectorAll('input[data-option-value]').forEach((input) => {
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !isAvailable(input.value));
        });
        group.querySelectorAll('select option').forEach((option) => {
          const available = isAvailable(option.value);
          option.toggleAttribute('data-unavailable', !available);
          const base = option.value;
          option.textContent = available ? base : `${base} — ${strings.unavailable || 'Unavailable'}`;
        });
      });
    }

    renderPrice(variant) {
      const el = this.querySelector('[data-product-price]');
      if (!el) return;
      if (!variant) {
        el.innerHTML = '';
        return;
      }
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      el.innerHTML = `<div class="price${onSale ? ' price--on-sale' : ''}"><span class="price__current">${theme.formatMoney(variant.price)}</span>${
        onSale ? `<s class="price__compare">${theme.formatMoney(variant.compare_at_price)}</s>` : ''
      }</div>`;
    }

    renderButton(variant, form) {
      if (!form) return;
      const button = form.querySelector('[data-add-to-cart]');
      const label = form.querySelector('[data-add-to-cart-label]');
      if (!button || !label) return;
      if (!variant) {
        button.disabled = true;
        label.textContent = strings.unavailable || 'Unavailable';
      } else if (!variant.available) {
        button.disabled = true;
        label.textContent = button.dataset.labelSoldOut || strings.soldOut || 'Sold out';
      } else {
        button.disabled = false;
        label.textContent = button.dataset.labelAdd || strings.addToCart || 'Add to cart';
      }
    }

    initReadMore() {
      this.querySelectorAll('[data-read-more]').forEach((text) => {
        const toggle = text.parentElement.querySelector('[data-read-more-toggle]');
        if (!toggle) return;
        requestAnimationFrame(() => {
          if (text.scrollHeight > text.clientHeight + 8) {
            text.classList.add('is-collapsed');
            toggle.hidden = false;
          } else {
            text.classList.add('is-expanded');
          }
        });
        toggle.addEventListener('click', () => {
          text.classList.remove('is-collapsed');
          text.classList.add('is-expanded');
          toggle.hidden = true;
        });
      });
    }

    initShare() {
      const button = this.querySelector('[data-share-url]');
      if (!button) return;
      button.addEventListener('click', async () => {
        const url = button.dataset.shareUrl;
        if (navigator.share) {
          try {
            await navigator.share({ title: button.dataset.shareTitle, url });
          } catch {
            /* dismissed */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          theme.toast && theme.toast(strings.linkCopied || 'Link copied');
        } catch {
          window.prompt('Copy link', url);
        }
      });
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-gallery-slider]');
      if (!this.slider) return;
      this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));
      this.slider.addEventListener('scroll', theme.debounce(() => this.onScroll(), 60), { passive: true });
      this.querySelectorAll('[data-zoom-index]').forEach((button) =>
        button.addEventListener('click', () => this.openLightbox(Number(button.dataset.zoomIndex)))
      );
      if (this.lightbox) {
        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.closeLightbox());
        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.closeLightbox();
        });
        this.lightbox.addEventListener('close', () => theme.unlockScroll && theme.unlockScroll());
      }
      // Stacked layout: highlight the thumbnail of the image in view
      if (this.dataset.layout === 'stacked' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (this.isSlider) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(Number(entry.target.dataset.index));
            });
          },
          { rootMargin: '-40% 0px -55% 0px' }
        );
        this.items.forEach((item) => this.observer.observe(item));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get items() {
      return [...this.slider.children];
    }

    get isSlider() {
      return getComputedStyle(this.slider).overflowX !== 'visible';
    }

    goTo(index, smooth = true) {
      const item = this.items.find((el) => Number(el.dataset.index) === index) || this.items[index];
      if (!item) return;
      if (this.isSlider) {
        this.slider.scrollTo({ left: item.offsetLeft - this.slider.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      } else if (this.dataset.layout === 'stacked') {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-visible-height')) || 0;
        window.scrollTo({ top: item.getBoundingClientRect().top + window.scrollY - header - 24, behavior: smooth ? 'smooth' : 'auto' });
      } else {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      this.setActive(Number(item.dataset.index));
    }

    setActive(index) {
      this.items.forEach((item) => item.classList.toggle('is-active', Number(item.dataset.index) === index));
      this.thumbs.forEach((thumb) => {
        const active = Number(thumb.dataset.thumbIndex) === index;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && thumb.parentElement.parentElement.scrollWidth > thumb.parentElement.parentElement.clientWidth) {
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });
      if (this.counter) {
        const position = this.items.findIndex((item) => Number(item.dataset.index) === index);
        this.counter.textContent = String(position + 1);
      }
    }

    onScroll() {
      if (!this.isSlider) return;
      const position = Math.round(this.slider.scrollLeft / this.slider.clientWidth);
      const item = this.items[position];
      if (item) this.setActive(Number(item.dataset.index));
    }

    showMedia(mediaId) {
      const item = this.items.find((el) => String(el.dataset.mediaId) === String(mediaId));
      if (!item) return;
      if (!this.isSlider && (this.dataset.layout === 'grid' || this.dataset.layout === 'stacked')) {
        // Show the variant's image first without moving the page
        this.slider.prepend(item);
        this.setActive(Number(item.dataset.index));
      } else {
        this.goTo(Number(item.dataset.index));
      }
    }

    openLightbox(index) {
      if (!this.lightbox) return;
      this.lightbox.showModal();
      theme.lockScroll && theme.lockScroll();
      const target = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
      if (target) {
        target.querySelectorAll('img').forEach((img) => (img.loading = 'eager'));
        requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      }
    }

    closeLightbox() {
      if (this.lightbox && this.lightbox.open) this.lightbox.close();
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- Product form: validation + AJAX add to cart ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[data-add-to-cart]');
      this.error = this.form.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    personalizationFields() {
      return [...this.form.elements].filter((el) => el.hasAttribute && el.hasAttribute('data-personalization'));
    }

    validate() {
      let firstInvalid = null;
      this.personalizationFields().forEach((el) => {
        const wrapper = el.closest('personalization-field');
        let valid = true;
        if (el.required) {
          if (el.type === 'checkbox') valid = el.checked;
          else if (el.type === 'file') valid = el.files && el.files.length > 0;
          else valid = el.value.trim().length > 0;
        }
        if (wrapper && wrapper.hasError) valid = false;
        if (wrapper) wrapper.classList.toggle('is-invalid', !valid);
        el.setAttribute('aria-invalid', valid ? 'false' : 'true');
        if (!valid && !firstInvalid) firstInvalid = el;
      });
      if (firstInvalid) {
        const target = firstInvalid.closest('personalization-field') || firstInvalid;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstInvalid.focus({ preventScroll: true }), 400);
      }
      return !firstInvalid;
    }

    buildFormData() {
      const source = new FormData(this.form);
      const data = new FormData();
      for (const [key, value] of source.entries()) {
        if (key.startsWith('properties[')) {
          if (value instanceof File && value.size === 0) continue;
          if (typeof value === 'string' && value.trim() === '') continue;
        }
        data.append(key, value);
      }
      return data;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled || this.button.classList.contains('is-loading')) return;
      if (this.error) this.error.hidden = true;
      if (!this.validate()) return;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      try {
        await theme.cart.add(this.buildFormData());
        if (theme.settings && theme.settings.cartType === 'page') {
          window.location.href = window.routes.cart_url;
        } else {
          theme.cart.open();
        }
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message || strings.cartError;
          this.error.hidden = false;
        } else {
          theme.toast(error.message || strings.cartError, 'error');
        }
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Personalization fields ---------- */
  class PersonalizationField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-personalization]');
      if (!this.input) return;
      this.counter = this.querySelector('[data-counter]');
      const clear = () => {
        this.classList.remove('is-invalid');
        this.input.setAttribute('aria-invalid', 'false');
      };
      this.input.addEventListener('input', () => {
        clear();
        this.updateCounter();
      });
      this.input.addEventListener('change', clear);
      this.updateCounter();
      if (this.dataset.type === 'file') this.initUpload();
    }

    updateCounter() {
      if (!this.counter || !(this.input.maxLength > 0)) return;
      const remaining = this.input.maxLength - this.input.value.length;
      this.counter.textContent = (strings.charactersLeft || '[count]').replace('[count]', remaining);
      this.counter.classList.toggle('is-low', remaining <= Math.max(5, this.input.maxLength * 0.1));
    }

    initUpload() {
      this.box = this.querySelector('[data-upload]');
      this.dropzone = this.querySelector('[data-upload-dropzone]');
      this.preview = this.querySelector('[data-upload-preview]');
      this.icon = this.querySelector('[data-upload-icon]');
      this.status = this.querySelector('[data-upload-status]');
      this.labelText = this.querySelector('[data-upload-label]');
      this.nameText = this.querySelector('[data-upload-name]');
      this.defaultLabel = this.labelText ? this.labelText.textContent : '';
      this.defaultName = this.nameText ? this.nameText.textContent : '';

      this.input.addEventListener('change', () => this.onFile());
      ['dragenter', 'dragover'].forEach((type) =>
        this.dropzone.addEventListener(type, (event) => {
          event.preventDefault();
          this.dropzone.classList.add('is-dragging');
        })
      );
      ['dragleave', 'drop'].forEach((type) =>
        this.dropzone.addEventListener(type, () => this.dropzone.classList.remove('is-dragging'))
      );
      this.dropzone.addEventListener('drop', (event) => {
        event.preventDefault();
        if (event.dataTransfer && event.dataTransfer.files.length) {
          this.input.files = event.dataTransfer.files;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    setStatus(message, type) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.className = `upload__status${type ? ` upload__status--${type}` : ''}`;
    }

    onFile() {
      const file = this.input.files && this.input.files[0];
      this.hasError = false;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      if (!file) {
        this.preview.hidden = true;
        this.preview.innerHTML = '';
        this.icon.hidden = false;
        this.labelText.textContent = this.defaultLabel;
        this.nameText.textContent = this.defaultName;
        this.setStatus('');
        this.box.classList.remove('has-file');
        return;
      }
      const maxMb = Number(this.box.dataset.maxMb) || 20;
      if (file.size > maxMb * 1024 * 1024) {
        this.hasError = true;
        this.input.value = '';
        this.setStatus((strings.photoTooLarge || '').replace('[max]', maxMb), 'error');
        return;
      }
      this.box.classList.add('has-file');
      this.labelText.textContent = strings.photoChange || 'Change photo';
      this.nameText.textContent = file.name;
      this.previewUrl = URL.createObjectURL(file);
      const img = new Image();
      img.alt = '';
      img.onload = () => {
        this.preview.innerHTML = '';
        this.preview.appendChild(img);
        this.preview.hidden = false;
        this.icon.hidden = true;
        const longest = Math.max(img.naturalWidth, img.naturalHeight);
        const min = Number(this.box.dataset.minWidth) || 0;
        if (min && longest < min) {
          this.setStatus(
            (strings.photoTooSmall || '')
              .replace('[width]', img.naturalWidth)
              .replace('[height]', img.naturalHeight)
              .replace('[min]', min),
            'warning'
          );
        } else {
          this.setStatus(strings.photoReady || '', 'success');
        }
      };
      img.onerror = () => {
        this.preview.hidden = true;
        this.icon.hidden = false;
        this.setStatus(strings.photoReady || '', 'success');
      };
      img.src = this.previewUrl;
    }
  }
  if (!customElements.get('personalization-field')) customElements.define('personalization-field', PersonalizationField);

  /* ---------- Live text preview ---------- */
  class PersonalizationPreview extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.textEl = this.querySelector('[data-preview-text]');
      this.fontEl = this.querySelector('[data-preview-font]');
      this.frame = this.querySelector('[data-preview-frame]');
      this.placeholder = this.textEl.innerHTML;
      this.onInput = (event) => {
        if (event.target.matches && event.target.matches(`[form="${this.formId}"][data-preview-source], [form="${this.formId}"][data-font-select]`)) this.update();
      };
      document.addEventListener('input', this.onInput);
      document.addEventListener('change', this.onInput);
      this.closest('product-info')?.addEventListener('variant:change', () => this.updateFrame());
      this.update();
      this.updateFrame();
    }

    disconnectedCallback() {
      document.removeEventListener('input', this.onInput);
      document.removeEventListener('change', this.onInput);
    }

    update() {
      const sources = [...document.querySelectorAll(`[form="${this.formId}"][data-preview-source]`)];
      const lines = sources.map((el) => el.value.trim()).filter(Boolean);
      if (!lines.length) {
        this.textEl.innerHTML = this.placeholder;
      } else {
        this.textEl.innerHTML = '';
        lines.forEach((line, index) => {
          line.split(/\n+/).forEach((part) => {
            const span = document.createElement('span');
            span.className = `sign-preview__line${index === 0 ? ' sign-preview__line--primary' : ''}`;
            span.textContent = part;
            this.textEl.appendChild(span);
          });
        });
      }
      const font = document.querySelector(`[form="${this.formId}"][data-font-select]`);
      if (this.fontEl) {
        this.fontEl.hidden = !(font && font.value);
        if (font && font.value) this.fontEl.textContent = `${font.closest('personalization-field')?.querySelector('.field__label span')?.firstChild?.textContent?.trim() || 'Font'}: ${font.value}`;
      }
    }

    updateFrame() {
      const info = this.closest('product-info');
      const swatch = info && info.querySelector('.product-option--swatch .product-option__input:checked + label .swatch');
      const color = swatch ? getComputedStyle(swatch).getPropertyValue('--swatch-color').trim() : '';
      if (this.frame) this.frame.style.setProperty('--frame-color', color || '');
    }
  }
  if (!customElements.get('personalization-preview')) customElements.define('personalization-preview', PersonalizationPreview);

  /* ---------- Shipping estimate ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 0;
      const max = Math.max(min, Number(this.dataset.max) || 0);
      const business = this.dataset.business === 'true';
      const addDays = (days) => {
        const date = new Date();
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (!business || (day !== 0 && day !== 6)) added += 1;
        }
        return date;
      };
      const format = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'short', month: 'short', day: 'numeric' });
      const text = (this.dataset.template || '')
        .replace('[start]', format.format(addDays(min)))
        .replace('[end]', format.format(addDays(max)))
        .replace('[min]', min)
        .replace('[max]', max);
      const target = this.querySelector('[data-delivery-text]');
      if (target) target.textContent = text;
    }
  }
  if (!customElements.get('delivery-estimate')) customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.info = document.querySelector(`product-info[data-section="${this.dataset.section}"]`);
      this.buy = this.info && this.info.querySelector('[data-buy-buttons]');
      this.button = this.querySelector('[data-sticky-button]');
      if (!this.info || !this.buy || !this.button) return;
      this.hidden = false;
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = entry.boundingClientRect.bottom < 0;
        this.classList.toggle('is-visible', !entry.isIntersecting && scrolledPast);
        document.body.classList.toggle('has-sticky-atc', !entry.isIntersecting && scrolledPast);
      });
      this.observer.observe(this.buy);
      this.button.addEventListener('click', () => this.onClick());
      this.info.addEventListener('variant:change', (event) => this.update(event.detail.variant));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }

    missingDetails() {
      const form = document.getElementById(this.info.dataset.formId);
      if (!form) return null;
      return [...form.elements].find((el) => {
        if (!el.hasAttribute || !el.hasAttribute('data-personalization') || !el.required) return false;
        if (el.type === 'checkbox') return !el.checked;
        if (el.type === 'file') return !(el.files && el.files.length);
        return !el.value.trim();
      });
    }

    onClick() {
      const form = document.getElementById(this.info.dataset.formId);
      const missing = this.missingDetails();
      if (missing) {
        (missing.closest('personalization-field') || missing).scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => missing.focus({ preventScroll: true }), 400);
        return;
      }
      if (form) form.requestSubmit(form.querySelector('[data-add-to-cart]'));
    }

    update(variant) {
      const price = this.querySelector('[data-sticky-price]');
      const title = this.querySelector('[data-sticky-variant]');
      if (price) price.innerHTML = variant ? theme.formatMoney(variant.price) : '';
      if (title && variant) title.textContent = variant.title === 'Default Title' ? '' : variant.title;
      if (this.info.dataset.requiresPersonalization !== 'true') {
        const main = this.info.querySelector('[data-add-to-cart]');
        const addLabel = (main && main.dataset.labelAdd) || strings.addToCart;
        const soldOutLabel = (main && main.dataset.labelSoldOut) || strings.soldOut;
        this.button.disabled = !variant || !variant.available;
        this.button.textContent = !variant ? strings.unavailable : variant.available ? addLabel : soldOutLabel;
      }
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- Recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const fresh = theme.parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.querySelector('.card')) {
              this.innerHTML = fresh.innerHTML;
              if (theme.initReveal) theme.initReveal(this);
            } else {
              const section = this.closest('.shopify-section');
              if (section) section.hidden = true;
            }
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
})();
